import type { ReactElement } from "react"
import VideoAtm from "@onestore/hel/dist/components/atoms/VideoAtm"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import type Theme from "@onestore/hel/dist/typings/theme"
import ModalWithAgreementsLink from "@gatsby-plugin-checkout/components/ModalWithAgreementsLink"
import TermLink from "@gatsby-plugin-checkout/components/TermLink"
import TermModalLink from "@gatsby-plugin-checkout/components/TermModalLink"
import Countdown from "@gatsby-plugin-generic-page/components/Countdown"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import DomainEmbeddedPrice from "@gatsby-plugin-generic-page/components/DomainEmbeddedPrice"
import PriceTypeContextText from "@gatsby-plugin-generic-page/components/PriceTypeContextText"
import SaleConfigurationPrice from "@gatsby-plugin-generic-page/components/SaleConfigurationPrice"
import StaticPrice from "@gatsby-plugin-generic-page/components/StaticPrice"
import type { Counter as CounterType } from "@gatsby-plugin-generic-page/fragments/counter"
import type { CtaElement as CtaElementType } from "@gatsby-plugin-generic-page/fragments/ctaElement"
import type { PriceTypeContextTextData } from "@gatsby-plugin-generic-page/fragments/priceTypeContextText"
import type { Tooltip as TooltipProps } from "@gatsby-plugin-generic-page/fragments/tooltip"
import type { Video } from "@gatsby-plugin-generic-page/fragments/video"
import { typeMatches } from "@onestore-graphql"
import Tooltip from "~/components/Tooltip"
import type { ModalTermsLink } from "~/fragments/modalTermsLink"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import type { StaticPrice as StaticPriceType } from "~/fragments/staticPrice"
import type { TermLink as TermLinkType } from "~/fragments/termLink"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"
import assetsSquidexToVideoObject from "~/lib/assetsSquidexToVideoObject"
import isEmpty from "~/lib/isEmpty"
import type {
  CloudBlueDomain,
  SquidexMarkdownEmbeddedContent,
} from "~/types/squidex"
import { ContentType } from "./contentType"

export type EmbeddedContentProps = SquidexMarkdownEmbeddedContent & {
  referenceId: string
  typography?: Theme.Typography
  emphasis?: Theme.Emphasis
}

function EmbeddedContent({
  contents,
  referenceId,
  typography,
  emphasis,
}: EmbeddedContentProps): ReactElement<EmbeddedContentProps> | null {
  const reference = contents?.find((content) => content.id === referenceId)

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.VIDEO)
  ) {
    const { flatData } = reference as Video

    if (isEmpty(flatData.video)) {
      return null
    }

    const videoProps = {
      ...flatData,
      video: assetsSquidexToVideoObject(flatData.video),
      fallbackImg: assetsSquidexToImageObject(flatData.fallbackImage, ""),
      poster: flatData.poster ? flatData.poster[0].url : undefined,
    }

    return (
      <PushOrg bottomSpace={4} topSpace={4} hasNoReset>
        <VideoAtm {...videoProps} disablePictureInPicture />
      </PushOrg>
    )
  }

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.CTA_ELEMENT)
  ) {
    return (
      <CtaElement
        typography={typography}
        ctaElement={[reference as CtaElementType]}
      />
    )
  }

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.COUNTER)
  ) {
    const { flatData } = reference as CounterType

    return <Countdown isSmall {...flatData} />
  }

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.SALE_CONFIGURATION)
  ) {
    const { flatData } = reference as SaleConfiguration

    return <SaleConfigurationPrice saleConfigurationFlatData={flatData} />
  }

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.STATIC_PRICE)
  ) {
    const staticPrices = reference as StaticPriceType

    return <StaticPrice staticPrice={staticPrices} />
  }

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.CLOUDBLUE_DOMAIN)
  ) {
    const { flatData } = reference as CloudBlueDomain

    return <DomainEmbeddedPrice domainFlatData={flatData} />
  }

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.PRICE_TYPE_CONTEXT_TEXT)
  ) {
    const { flatData } = reference as PriceTypeContextTextData

    return <PriceTypeContextText flatData={flatData} />
  }

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.TOOLTIP)
  ) {
    const { flatData } = reference as TooltipProps
    const { text, ...restData } = flatData

    return (
      <Tooltip hasInheritedLabelTypography {...restData}>
        {text}
      </Tooltip>
    )
  }

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.MODAL_TERMS_LINK)
  ) {
    const { flatData } = reference as ModalTermsLink

    return (
      <ModalWithAgreementsLink
        {...flatData}
        typography={typography}
        emphasis={emphasis}
      />
    )
  }

  if (
    !isEmpty(reference) &&
    typeMatches(reference.__typename, ContentType.TERM_LINK)
  ) {
    const { flatData } = reference as TermLinkType
    const { isDisplayInModal, ...restFlatData } = flatData

    if (isDisplayInModal) {
      return (
        <TermModalLink
          {...restFlatData}
          typography={typography}
          emphasis={emphasis}
        />
      )
    }

    return (
      <TermLink {...restFlatData} typography={typography} emphasis={emphasis} />
    )
  }

  return null
}

export default EmbeddedContent
